import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import React from 'react'
import { getOnDemandBreakImage, OnDemandCityConfig } from 'src/utils/onDemand'

interface IProps {
  /** City-page variant: picks a break photo that doesn't repeat the city's
   *  hero. When unset (parent page), the helper falls back to the parent
   *  pairing. */
  coverage?: OnDemandCityConfig
}

/**
 * Full-bleed photo break — the "Tú abres la puerta. Nosotros hacemos el
 * resto." moment, set over the photo instead of beside it. It is the one
 * image-led pause between the process section and the pricing sections.
 *
 * Photo from `getOnDemandBreakImage(citySlug)` (see the allocation table on
 * `ON_DEMAND_PHOTOS`). Scrim: bottom-up on mobile (copy sits at the foot of
 * the photo), right-to-left from `md:` (copy on the right rail, the subject
 * left of centre stays clear).
 */
const OnDemandPhotoBreak = ({ coverage }: IProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')

  const photoSrc = getOnDemandBreakImage(coverage?.canonicalSlug)

  return (
    <section
      aria-labelledby="photo-break-heading"
      className="relative isolate overflow-hidden bg-ink-800"
    >
      <div className="absolute inset-0 -z-10">
        <Image
          src={photoSrc}
          alt={t('PHOTO_BREAK.PHOTO_ALT')}
          fill
          sizes="100vw"
          className="object-cover object-[35%_center] md:object-[35%_22%]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-800/95 from-15% via-ink-800/45 via-55% to-ink-800/5 md:bg-gradient-to-l md:from-ink-800/90 md:from-30% md:via-ink-800/45 md:via-60% md:to-transparent" />
      </div>

      <div className="mx-auto flex min-h-[480px] max-w-7xl items-end px-5 py-12 md:min-h-[520px] md:items-center md:justify-end md:px-10 md:py-24">
        <div className="max-w-md">
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {t('PHOTO_BREAK.EYEBROW')}
          </p>
          <h2
            id="photo-break-heading"
            className="mt-3 font-display text-[30px] leading-[1.08] font-medium tracking-[-0.015em] text-white-500 md:text-[44px]"
          >
            {`${t('PHOTO_BREAK.TITLE_BASE')} `}
            <span className="font-display text-blue-500 italic">
              {t('PHOTO_BREAK.TITLE_ACCENT')}
            </span>
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white-500/85 md:text-[17px]">
            {t('PHOTO_BREAK.BODY')}
          </p>
        </div>
      </div>
    </section>
  )
}

export default React.memo(OnDemandPhotoBreak)
